import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceV2FilterOptionSuggestions } from './fetchInvoiceV2FilterOptionSuggestions';
import {
  type InvoiceV2FilterOptionSuggestionsPayload,
  type InvoiceV2FilterOptionSuggestionsResponse,
} from './types';

/**
 * Generates the query options for the invoice cost-center and site filter
 * suggestions so they can be shared between the useQuery hook and imperative
 * fetching.
 *
 * @param kind - Whether to suggest cost centers or sites.
 * @param queryParams - Optional name substring and result limit.
 * @param options - Additional options for the query.
 */
export const getInvoiceV2FilterOptionSuggestionsQueryOptions = <
  TData = InvoiceV2FilterOptionSuggestionsResponse,
>(
  { kind, queryParams = {} }: InvoiceV2FilterOptionSuggestionsPayload,
  options?: Omit<
    UseQueryOptions<InvoiceV2FilterOptionSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchInvoiceV2FilterOptionSuggestions({ kind, queryParams }, { signal }),
    queryKey: queryKeysInvoice.getFilterOptionSuggestions({
      kind,
      ...queryParams,
    }),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for the cost centers or sites the caller may filter the
 * invoice list by.
 *
 * @param kind - Whether to suggest cost centers or sites.
 * @param queryParams - Optional name substring and result limit.
 * @param options - Additional options for the useQuery hook.
 */
// fallow-ignore-next-line unused-export -- canonical hook for the suggestion endpoints
export const useQueryInvoiceV2FilterOptionSuggestions = <
  TData = InvoiceV2FilterOptionSuggestionsResponse,
>(
  payload: InvoiceV2FilterOptionSuggestionsPayload,
  options?: Omit<
    UseQueryOptions<InvoiceV2FilterOptionSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getInvoiceV2FilterOptionSuggestionsQueryOptions<TData>(payload, options),
  );
